import { useCallback } from "react";
import {
  applyOperations, canBeBillable, createEvent, dayKey, dayStartOf, deleteOccurrence, editOccurrence, entryInvoice, isLive, logEntry, logPlanned, overlappingEntries,
  rateSnapshotFor, shiftDays, startTimer, stopTimer, type Dataset, type EditScope, type EventFields, type RepeatRule, type SyncOperation, type TimeEntry
} from "@otrack/domain";
import { projectLabel } from "../commands";
import { useApp, useDataset, useFactory } from "../context";
import { formatClock, formatDuration, MINUTE } from "../time";
import { writableCalendars } from "./google";
import { googleCreate, googleDelete, googleEdit, googleMove, type GooglePatch, type PendingChange } from "./google-events";
import { isEditablePlan, isEntry, type CalItem, type EntryItem, type PlanItem } from "./model";

export const SNAP = 5 * MINUTE;

/** A block being made on the Calendar, not saved yet. */
export interface Draft {
  kind: "entry" | "event";
  start: number;
  end: number;
  allDay: boolean;
}

/** What a copy holds: enough to recreate each item at a new time. */
export interface Clip {
  kind: "entry" | "event";
  offset: number;
  length: number;
  allDay: boolean;
  title: string;
  notes: string;
  projectId: string | null;
  taskId: string | null;
  billable: boolean;
  /** The Google calendar an event was copied from; null for ontrack Events and entries. */
  calendarId: string | null;
  location: string;
}

/** Fields of a planned item a change may set: an Event's, plus a Google event's location and calendar. */
export type PlanPatch = Partial<EventFields> & { location?: string; calendarId?: string };

/** Where a new event goes: null for ontrack, or a Google calendar's id. */
export type Destination = string | null;

export type AskScope = (options: { allowThis: boolean; verb: "change" | "delete" }) => Promise<EditScope | null>;

/** Why a span cannot hold a Time Entry, or null when it can. */
export function entryConflict(dataset: Dataset, start: number, end: number, now: number, ignore: ReadonlySet<string> = new Set()): string | null {
  if (end > now + 30_000) return "Time can only be logged up to now.";
  const other = overlappingEntries(dataset, start, end, now).find((entry) => !ignore.has(entry.id));
  if (other) return `That overlaps ${projectLabel(dataset, other.projectId)}, ${formatClock(other.start)} to ${other.end === null ? "now" : formatClock(other.end)}.`;
  return null;
}

/**
 * Where moved entries can go: the requested shift if it is free, otherwise the nearest shift that
 * puts them against a neighbour's edge (or against now). `blocked` means the pointer sits so far
 * inside other time that the move should snap back instead of jumping there.
 */
export function resolveEntryShift(dataset: Dataset, moving: readonly TimeEntry[], delta: number, now: number): { delta: number; blocked: boolean } {
  const ignore = new Set(moving.map((entry) => entry.id));
  const fits = (shift: number) => moving.every((entry) => entryConflict(dataset, entry.start + shift, (entry.end ?? now) + shift, now, ignore) === null);
  if (fits(delta)) return { delta, blocked: false };
  const candidates = new Set<number>();
  for (const entry of moving) {
    const end = entry.end ?? now;
    candidates.add(now - end);
    for (const other of overlappingEntries(dataset, entry.start + delta - 6 * 3_600_000, end + delta + 6 * 3_600_000, now)) {
      if (ignore.has(other.id)) continue;
      candidates.add((other.end ?? now) - entry.start);
      candidates.add(other.start - end);
    }
  }
  const best = [...candidates].filter(fits).sort((a, b) => Math.abs(a - delta) - Math.abs(b - delta))[0];
  const longest = Math.max(...moving.map((entry) => (entry.end ?? now) - entry.start));
  if (best === undefined) return { delta: 0, blocked: true };
  return { delta: best, blocked: Math.abs(best - delta) > Math.max(longest, 30 * MINUTE) };
}

/** How far an entry's edge can go before it meets a neighbour, the day's limits, or now. */
export function entryEdgeLimits(dataset: Dataset, entry: TimeEntry, now: number): { earliest: number; latest: number } {
  let earliest = -Infinity;
  let latest = now;
  for (const other of Object.values(dataset.timeEntries)) {
    if (!isLive(other) || other.id === entry.id) continue;
    const otherEnd = other.end ?? now;
    if (otherEnd <= entry.start) earliest = Math.max(earliest, otherEnd);
    if (other.start >= (entry.end ?? now)) latest = Math.min(latest, other.start);
  }
  return { earliest, latest };
}

export function itemLabel(dataset: Dataset, item: CalItem): string {
  if (isEntry(item)) return item.title || projectLabel(dataset, item.projectId);
  return item.title || "Untitled event";
}

function eventFieldsOf(item: PlanItem, start: number, end: number): Partial<EventFields> {
  if (item.allDay) return { allDay: true, startDay: dayKey(start), endDay: dayKey(end - 1) };
  return { start, end };
}

/** An Event-style patch as a Google one: all-day days become the span they cover. */
function toGooglePatch(patch: PlanPatch): GooglePatch {
  const result: GooglePatch = {};
  if (patch.title !== undefined) result.title = patch.title;
  if (patch.notes !== undefined) result.notes = patch.notes;
  if (patch.location !== undefined) result.location = patch.location;
  if (patch.projectId !== undefined) result.projectId = patch.projectId;
  if (patch.taskId !== undefined) result.taskId = patch.taskId;
  if (patch.repeat !== undefined) result.repeat = patch.repeat;
  if (patch.allDay !== undefined) result.allDay = patch.allDay;
  if (patch.allDay && patch.startDay && patch.endDay) {
    result.start = dayStartOf(patch.startDay);
    result.end = shiftDays(dayStartOf(patch.endDay), 1);
  } else {
    if (patch.start !== undefined) result.start = patch.start;
    if (patch.end !== undefined) result.end = patch.end;
  }
  return result;
}

/** Calendar commands. Every change goes through `act`, so it shows a toast and can be undone. */
export function useCalendarCommands(askScope: AskScope) {
  const { act, notify, store, google } = useApp();
  const dataset = useDataset();
  const factory = useFactory();

  const fresh = useCallback(() => store.getState().dataset, [store]);
  const googleEvents = useCallback((calendarId: string) => google?.store.getState().events[calendarId] ?? {}, [google]);
  /** A Google calendar new events may go to, when `id` is one. */
  const writable = useCallback((id: string | null) => (id && google ? writableCalendars(google.store.getState().calendars).find((calendar) => calendar.id === id) ?? null : null), [google]);

  const createDraft = useCallback(async (draft: Draft, fields: { title: string; notes?: string; location?: string; projectId: string | null; taskId: string | null; billable?: boolean; destination?: Destination; repeat?: RepeatRule | null }): Promise<string | null> => {
    const now = Date.now();
    const data = fresh();
    if (draft.kind === "entry") {
      const end = Math.min(draft.end, now);
      const problem = entryConflict(data, draft.start, end, now);
      if (problem) return problem;
      if (end - draft.start < MINUTE) return "That is too short to log.";
      const operations = logEntry(data, { projectId: fields.projectId, taskId: fields.taskId, description: fields.title, ...(fields.billable === undefined ? {} : { billable: fields.billable }) }, draft.start, end, factory);
      await act(operations, `Logged ${formatDuration(end - draft.start)} on ${projectLabel(data, fields.projectId)}`);
      return null;
    }
    const message = `Planned ${fields.title || "an event"}${draft.allDay ? "" : `, ${formatClock(draft.start)} to ${formatClock(draft.end)}`}`;
    const calendar = writable(fields.destination ?? null);
    if (calendar) {
      const change = googleCreate(calendar.id, { title: fields.title, notes: fields.notes ?? "", location: fields.location ?? "", projectId: fields.projectId, taskId: fields.taskId, allDay: draft.allDay, start: draft.start, end: draft.end, repeat: fields.repeat ?? null });
      await act([], `${message} in ${calendar.name}`, { google: [change] });
      return null;
    }
    const created = createEvent({
      title: fields.title, notes: fields.notes ?? "", projectId: fields.projectId, taskId: fields.taskId, allDay: draft.allDay,
      start: draft.start, end: draft.end, startDay: draft.allDay ? dayKey(draft.start) : null, endDay: draft.allDay ? dayKey(draft.end - 1) : null, repeat: fields.repeat ?? null
    }, factory);
    await act(created.operations, message);
    return null;
  }, [act, factory, fresh, writable]);

  /** Applies a change to a planned occurrence, asking which occurrences when it repeats. */
  const changePlan = useCallback(async (item: PlanItem, patch: PlanPatch, message: string, forceScope?: EditScope): Promise<boolean> => {
    const repeatChanged = patch.repeat !== undefined;
    if (item.kind === "google") {
      const plan = item.google;
      if (!plan?.editable) return false;
      // An occurrence whose series isn't on this device can only change on its own.
      const seriesKnown = plan.occurrence.master !== null;
      const scope = item.recurring ? forceScope ?? (seriesKnown ? await askScope({ allowThis: !repeatChanged, verb: "change" }) : "this") : "all";
      if (!scope) return false;
      const { calendarId, ...fields } = patch;
      const changes: PendingChange[] = googleEdit(googleEvents(plan.calendar.id), plan.occurrence, toGooglePatch(fields), scope);
      if (calendarId && calendarId !== plan.calendar.id && writable(calendarId) && (!item.recurring || scope === "all")) changes.push(...googleMove(plan.occurrence, calendarId));
      if (changes.length) await act([], message, { google: changes });
      return true;
    }
    if (item.kind !== "event" || !item.occurrence) return false;
    const scope = item.recurring ? forceScope ?? await askScope({ allowThis: !repeatChanged, verb: "change" }) : "all";
    if (!scope) return false;
    const { location: _location, calendarId: _calendarId, ...fields } = patch;
    const operations = editOccurrence(fresh(), item.occurrence, fields, scope, factory, Date.now());
    if (operations.length) await act(operations, message);
    return true;
  }, [act, askScope, factory, fresh, googleEvents, writable]);

  const saveEntry = useCallback(async (item: EntryItem, patch: { title?: string; projectId?: string | null; taskId?: string | null; start?: number; end?: number | null; billable?: boolean }): Promise<string | null> => {
    if (item.locked) return "This entry is on a sent invoice.";
    const data = fresh();
    const now = Date.now();
    const entry = data.timeEntries[item.entry.id];
    if (!entry) return null;
    const start = patch.start ?? entry.start;
    const end = patch.end === undefined ? entry.end : patch.end;
    if (end !== null && end <= start) return "The end must be after the start.";
    const problem = entryConflict(data, start, end ?? now, now, new Set([entry.id]));
    if (problem) return problem;
    const next: Record<string, unknown> = {};
    if (patch.title !== undefined && patch.title !== entry.description) next["description"] = patch.title;
    if (start !== entry.start) next["start"] = start;
    if (end !== entry.end) next["end"] = end;
    if (patch.taskId !== undefined && patch.taskId !== entry.taskId) next["taskId"] = patch.taskId;
    const projectId = patch.projectId === undefined ? entry.projectId : patch.projectId;
    const project = projectId ? data.projects[projectId] : undefined;
    const billable = canBeBillable(project) && (patch.billable ?? entry.billable);
    if (projectId !== entry.projectId || billable !== entry.billable) {
      next["projectId"] = projectId;
      next["billable"] = billable;
      next["rateSnapshot"] = rateSnapshotFor(data, projectId, billable);
      if (projectId !== entry.projectId && patch.taskId === undefined) next["taskId"] = null;
    }
    if (Object.keys(next).length) await act([factory.op("timeEntry", entry.id, next)], "Entry updated");
    return null;
  }, [act, factory, fresh]);

  const removeItems = useCallback(async (items: readonly CalItem[]) => {
    const data = fresh();
    const now = Date.now();
    const operations: SyncOperation[] = [];
    const changes: PendingChange[] = [];
    let skipped = 0;
    const plans = items.filter((item): item is PlanItem => !isEntry(item) && isEditablePlan(item));
    const repeating = plans.filter((plan) => plan.recurring && (plan.kind === "event" || plan.google?.occurrence.master));
    const scope = repeating.length === 1 && plans.length === 1 ? await askScope({ allowThis: true, verb: "delete" }) : "this";
    if (!scope) return;
    let googleCount = 0;
    for (const item of items) {
      if (isEntry(item)) {
        const entry = data.timeEntries[item.entry.id];
        if (!entry || item.locked) { skipped += 1; continue; }
        const invoice = entryInvoice(data, entry);
        operations.push(factory.op("timeEntry", entry.id, { deletedAt: now, ...(entry.end === null ? { end: Math.max(entry.start, now) } : {}), ...(invoice ? { invoiceId: null } : {}) }));
      } else if (item.kind === "event" && item.occurrence) {
        operations.push(...deleteOccurrence(data, item.occurrence, item.recurring ? scope : "all", factory, now));
      } else if (item.google?.editable) {
        changes.push(...googleDelete(googleEvents(item.google.calendar.id), item.google.occurrence, item.recurring ? scope : "all"));
        googleCount += 1;
      } else skipped += 1;
    }
    if (!operations.length && !changes.length) return notify(skipped ? "Invitations, read-only calendars, and invoiced time can't be deleted here." : "Nothing to delete");
    const count = items.length - skipped;
    const only = items.find((item) => (isEntry(item) ? !item.locked : isEditablePlan(item)))!;
    const message = count === 1
      ? (googleCount ? `Deleted ${itemLabel(data, only)} from ${(only as PlanItem).google!.calendar.name}` : `Moved ${itemLabel(data, only)} to Trash`)
      : googleCount === count ? `Deleted ${count} events` : `Moved ${count - googleCount} items to Trash${googleCount ? `, deleted ${googleCount} from Google` : ""}`;
    await act(operations, message, { google: changes });
  }, [act, askScope, factory, fresh, googleEvents, notify]);

  /** Moves (or copies) items by the same amount of time. Entries move all together or not at all. */
  const shiftItems = useCallback(async (items: readonly CalItem[], delta: number, duplicate: boolean, dayDelta = 0): Promise<boolean> => {
    if (!delta && !duplicate) return true;
    const data = fresh();
    const now = Date.now();
    const entries = items.filter(isEntry).filter((item) => !item.locked && (!item.running || duplicate));
    const plans = items.filter((item): item is PlanItem => !isEntry(item));
    const shift = (ts: number, allDay: boolean) => allDay ? shiftDays(ts, dayDelta || Math.round(delta / 86_400_000)) : ts + delta;
    const operations: SyncOperation[] = [];
    const changes: PendingChange[] = [];
    for (const item of entries) {
      const entry = data.timeEntries[item.entry.id]!;
      const start = entry.start + delta;
      const end = (entry.end ?? now) + delta;
      const problem = entryConflict(data, start, end, now, duplicate ? new Set() : new Set(entries.map((other) => other.entry.id)));
      if (problem) { notify(problem); return false; }
      if (duplicate) operations.push(...logEntry(data, { projectId: entry.projectId, taskId: entry.taskId, description: entry.description, billable: entry.billable }, start, end, factory));
      else operations.push(factory.op("timeEntry", entry.id, { start, end }));
    }
    for (const plan of plans) {
      const start = shift(plan.start, plan.allDay);
      const end = shift(plan.end, plan.allDay);
      const google = plan.google;
      if (duplicate || !isEditablePlan(plan)) {
        // A copy stays in its Google calendar when it can; one of a read-only calendar becomes an ontrack Event.
        const calendar = google ? writable(google.calendar.id) : null;
        if (calendar) {
          changes.push(googleCreate(calendar.id, { title: plan.title, notes: plan.notes, location: google?.location ?? "", projectId: plan.projectId, taskId: plan.taskId, allDay: plan.allDay, start, end, repeat: null }));
          continue;
        }
        operations.push(...createEvent({
          title: plan.title, notes: plan.notes, projectId: plan.projectId, taskId: plan.taskId, allDay: plan.allDay, start, end,
          startDay: plan.allDay ? dayKey(start) : null, endDay: plan.allDay ? dayKey(end - 1) : null, repeat: null
        }, factory).operations);
      } else if (plan.recurring && plans.length === 1 && !entries.length) {
        return changePlan(plan, eventFieldsOf(plan, start, end), `Moved ${itemLabel(data, plan)}`);
      } else if (google) {
        changes.push(...googleEdit(googleEvents(google.calendar.id), google.occurrence, toGooglePatch(eventFieldsOf(plan, start, end)), "this"));
      } else if (plan.occurrence) {
        operations.push(...editOccurrence(data, plan.occurrence, eventFieldsOf(plan, start, end), "this", factory, now));
      }
    }
    if (!operations.length && !changes.length) return false;
    const label = items.length === 1 ? itemLabel(data, items[0]!) : `${items.length} items`;
    await act(operations, duplicate ? `Copied ${label}` : `Moved ${label}`, { google: changes });
    return true;
  }, [act, changePlan, factory, fresh, googleEvents, notify, writable]);

  const resizeItem = useCallback(async (item: CalItem, start: number, end: number) => {
    if (isEntry(item)) {
      const problem = await saveEntry(item, { start, ...(item.running ? {} : { end }) });
      if (problem) notify(problem);
      return;
    }
    if (isEditablePlan(item)) await changePlan(item, eventFieldsOf(item, start, end), `${itemLabel(fresh(), item)} now ${item.allDay ? "spans those days" : `${formatClock(start)} to ${formatClock(end)}`}`);
  }, [changePlan, fresh, notify, saveEntry]);

  /**
   * What remembering `projectId` for a Google event takes: on the event itself (the whole series)
   * when ontrack may change it, in Settings for an invitation or a read-only calendar.
   */
  const rememberProject = useCallback((data: Dataset, plan: PlanItem, projectId: string | null): { operations: SyncOperation[]; changes: PendingChange[] } => {
    const google = plan.google;
    if (!google || projectId === plan.projectId) return { operations: [], changes: [] };
    if (google.editable) return { operations: [], changes: googleEdit(googleEvents(google.calendar.id), google.occurrence, { projectId, taskId: null }, plan.recurring && google.occurrence.master ? "all" : "this") };
    const projects = { ...data.settings.googleProjects };
    if (projectId) projects[google.seriesKey] = projectId;
    else delete projects[google.seriesKey];
    return { operations: [factory.op("settings", "settings", { googleProjects: projects })], changes: [] };
  }, [factory, googleEvents]);

  /** Logs a planned item as worked, remembering the Project chosen for a Google series. */
  const logPlan = useCallback(async (plan: PlanItem, projectId = plan.projectId) => {
    const data = fresh();
    const result = logPlanned(data, { projectId, taskId: projectId === plan.projectId ? plan.taskId : null, description: plan.title, eventId: plan.linkId, eventOccurrence: plan.linkOccurrence, start: plan.start, end: plan.end }, factory, Date.now());
    if ("error" in result) return notify(result.error);
    const remembered = projectId ? rememberProject(data, plan, projectId) : { operations: [], changes: [] };
    await act([...result.operations, ...remembered.operations], `Logged ${formatDuration(result.logged)} from ${plan.title || "the plan"}`, { google: remembered.changes });
  }, [act, factory, fresh, notify, rememberProject]);

  const startPlan = useCallback(async (plan: PlanItem, projectId = plan.projectId) => {
    const data = fresh();
    const operations = startTimer(data, { projectId, taskId: projectId === plan.projectId ? plan.taskId : null, description: plan.title, eventId: plan.linkId, eventOccurrence: plan.linkOccurrence }, factory, Date.now());
    const remembered = projectId ? rememberProject(data, plan, projectId) : { operations: [], changes: [] };
    await act([...operations, ...remembered.operations], `Started ${plan.title || projectLabel(data, projectId)}`, { google: remembered.changes });
  }, [act, factory, fresh, rememberProject]);

  const stop = useCallback(async () => {
    await act(stopTimer(fresh(), factory, Date.now()), "Timer stopped");
  }, [act, factory, fresh]);

  const setProject = useCallback(async (items: readonly CalItem[], projectId: string | null) => {
    const data = fresh();
    const operations: SyncOperation[] = [];
    const changes: PendingChange[] = [];
    for (const item of items) {
      if (isEntry(item)) {
        if (item.locked) continue;
        const project = projectId ? data.projects[projectId] : undefined;
        const billable = canBeBillable(project) && item.entry.billable;
        operations.push(factory.op("timeEntry", item.entry.id, { projectId, taskId: null, billable, rateSnapshot: rateSnapshotFor(data, projectId, billable) }));
      } else if (item.kind === "event" && item.occurrence) {
        operations.push(...editOccurrence(data, item.occurrence, { projectId, taskId: null }, item.recurring ? "all" : "this", factory, Date.now()));
      } else if (item.google) {
        const remembered = rememberProject(applyOperations(data, operations), item, projectId);
        operations.push(...remembered.operations);
        changes.push(...remembered.changes);
      }
    }
    if (operations.length || changes.length) await act(operations, `Moved to ${projectLabel(data, projectId)}`, { google: changes });
  }, [act, factory, fresh, rememberProject]);

  const toggleBillable = useCallback(async (items: readonly EntryItem[]) => {
    const data = fresh();
    const target = !items.every((item) => item.billable);
    const operations = items.filter((item) => !item.locked && canBeBillable(item.projectId ? data.projects[item.projectId] : undefined)).map((item) => factory.op("timeEntry", item.entry.id, { billable: target, rateSnapshot: rateSnapshotFor(data, item.projectId, target) }));
    if (!operations.length) return notify("Those entries cannot be billable.");
    await act(operations, target ? "Marked billable" : "Marked non-billable");
  }, [act, factory, fresh, notify]);

  /** Recreates copied items with the earliest one starting at `at`. Google copies stay in their calendar. */
  const paste = useCallback(async (clips: readonly Clip[], at: number) => {
    if (!clips.length) return notify("Nothing copied yet");
    const data = fresh();
    const now = Date.now();
    const operations: SyncOperation[] = [];
    const changes: PendingChange[] = [];
    let working = data;
    for (const clip of clips) {
      const start = at + clip.offset;
      const end = start + clip.length;
      if (clip.kind === "entry") {
        const problem = entryConflict(working, start, end, now);
        if (problem) return notify(problem);
        const created = logEntry(working, { projectId: clip.projectId, taskId: clip.taskId, description: clip.title, billable: clip.billable }, start, end, factory);
        operations.push(...created);
        // Later pasted entries must not overlap the ones just placed.
        working = applyOperations(working, created);
        continue;
      }
      const calendar = writable(clip.calendarId);
      if (calendar) {
        changes.push(googleCreate(calendar.id, { title: clip.title, notes: clip.notes, location: clip.location, projectId: clip.projectId, taskId: clip.taskId, allDay: clip.allDay, start, end, repeat: null }));
        continue;
      }
      operations.push(...createEvent({
        title: clip.title, notes: clip.notes, projectId: clip.projectId, taskId: clip.taskId, allDay: clip.allDay, start, end,
        startDay: clip.allDay ? dayKey(start) : null, endDay: clip.allDay ? dayKey(end - 1) : null, repeat: null
      }, factory).operations);
    }
    await act(operations, clips.length === 1 ? `Pasted ${clips[0]!.title || "a copy"}` : `Pasted ${clips.length} items`, { google: changes });
  }, [act, factory, fresh, notify, writable]);

  return { dataset, createDraft, changePlan, saveEntry, removeItems, shiftItems, resizeItem, logPlan, startPlan, stop, setProject, toggleBillable, paste };
}

export function clipsOf(items: readonly CalItem[]): Clip[] {
  if (!items.length) return [];
  const first = Math.min(...items.map((item) => item.start));
  return items.map((item) => isEntry(item)
    ? { kind: "entry", offset: item.start - first, length: item.end - item.start, allDay: false, title: item.title, notes: "", projectId: item.projectId, taskId: item.taskId, billable: item.billable, calendarId: null, location: "" }
    : { kind: "event", offset: item.start - first, length: item.end - item.start, allDay: item.allDay, title: item.title, notes: item.notes, projectId: item.projectId, taskId: item.taskId, billable: false, calendarId: item.google?.calendar.id ?? null, location: item.google?.location ?? "" });
}
